import { h, JSX } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useBlackBarSizeDetector } from '../../creative-validators';
import { useAdParameters } from '../hooks/useAdParameters';
import { useAdSize } from '../hooks/useAdSize';
import { useClickthru } from '../hooks/useClickthru';
import { useControls } from '../hooks/useControls';
import { useCustomizePlayer } from '../hooks/useCustomizePlayer';
import { useHideCredits } from '../hooks/useHideCredits';
import { useHideVolumeOnEnd } from '../hooks/useHideVolumeOnEnd';
import { useImageRatio } from '../hooks/useImageRatio';
import { useManualDuration } from '../hooks/useManualDuration';
import { useMaxElementHeight } from '../hooks/useMaxElementHeight';
import { useMoveVideoSlotInContainer } from '../hooks/useMoveVideoSlotInContainer';
import { useOptimizedAsset } from '../hooks/useOptimizedAsset';
import { useResizeObserver } from '../hooks/useResizeObserver';
import { useSetAdBackgroundColor } from '../hooks/useSetAdBackgroundColor';
import { useSetAdIsDisplay } from '../hooks/useSetAdIsDisplay';
import { useSetVideoSlotURI } from '../hooks/useSetVideoSlotURI';
import { useSetVideoSlotVolume } from '../hooks/useSetVideoSlotVolume';
import { useShowEndScreen } from '../hooks/useShowEndScreen';
import { useStopAdOnVideoEnd } from '../hooks/useStopAdOnVideoEnd';
import { useUpdateSlotSizeWithMainImage, useUpdateSlotSizeWithVideoSlot } from '../hooks/useUpdateSlotSize';
import { useUpdateTime } from '../hooks/useUpdateTime';
import { useVideoRatio } from '../hooks/useVideoRatio';
import { useVideoSlotPlayPause } from '../hooks/useVideoSlotPlayPause';
import { useVPAIDPlayState } from '../hooks/useVPAIDPlayState';
import { ImageCropWrapper } from '../image-crop-wrapper';
import { isRtlLanguage } from '../rtl-languages';
import { AssetComponent, AssetComponentId } from './asset-component';
import { CreativeComponent } from './creative-component';
import { PX, shouldBarsBeVisible, style } from './lib';

const isRTL = (c: any) => isRtlLanguage(c.ctaLanguage);
const adMargin = 2;
interface SocialCreativeProps {
  videoSlot?: any;
  header: JSX.Element;
  footer: JSX.Element;
}

export function SocialVideoCreative({ videoSlot, header, footer }: SocialCreativeProps) {
  const { config } = useAdParameters<any>();
  useHideCredits();
  const assetRef = useRef<HTMLDivElement>(null);

  const { playState, stopVPAID } = useVPAIDPlayState();
  useVideoSlotPlayPause(playState, videoSlot);

  useStopAdOnVideoEnd(videoSlot, stopVPAID);
  useUpdateTime(videoSlot);
  useSetVideoSlotVolume(videoSlot);
  useHideVolumeOnEnd(videoSlot);
  useSetAdBackgroundColor('#fff');
  useSetAdIsDisplay(false);

  const mainVideoUri = useOptimizedAsset(config.asset.video);
  const endScreenUri = useOptimizedAsset(config.end_screen);
  useSetVideoSlotURI(mainVideoUri, videoSlot);
  const videoRatio = useVideoRatio(videoSlot);
  const assetRatio = useMemo(() => videoRatio && Math.max(0.8, videoRatio), [videoRatio]);
  const adSize = useAdSize();
  useMoveVideoSlotInContainer(adSize, assetRef, videoSlot, videoRatio);
  useCustomizePlayer(adSize, assetRef);

  const onClick = useClickthru(config.destination_url);
  const showEndScreen = useShowEndScreen();
  const isVertical = videoRatio > 0.8;
  const adRef = useRef<HTMLElement>(null);
  useUpdateSlotSizeWithVideoSlot(adRef.current, videoSlot);

  const assetContainerStyle = useMemo(
    () => ({
      width: adSize.width - adMargin,
      height: (adSize.width - adMargin) / (assetRatio || 1),
    }),
    [assetRatio, adSize]
  );

  const videoSlotContainerSize = useResizeObserver(assetRef.current);

  useBlackBarSizeDetector(
    videoSlotContainerSize.width,
    videoSlotContainerSize.height,
    videoSlot?.videoWidth ?? 0,
    videoSlot?.videoHeight ?? 0
  );

  useEffect(() => {
    if (showEndScreen) videoSlot?.pause?.();
    style(videoSlot, {
      visibility: showEndScreen ? 'hidden' : 'visible',
    });
  }, [showEndScreen]);

  const endScreenStyle = useMemo(
    () => ({
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      width: '100%',
      height: '100%',
      visibility: showEndScreen ? 'visible' : 'hidden',
      backgroundColor: '#000',
    }),
    [showEndScreen]
  );

  return (
    <CreativeComponent onClick={onClick} isRTL={isRTL} adRef={adRef}>
      {header}
      <AssetComponent assetRef={assetRef} width={PX(assetContainerStyle.width)} height={PX(assetContainerStyle.height)}>
        <ImageCropWrapper asset={config.end_screen} alt={'end-screen'} style={endScreenStyle} />
      </AssetComponent>
      {footer}
    </CreativeComponent>
  );
}

export function SocialImageCreative({ videoSlot, header, footer }: SocialCreativeProps) {
  const { updateTime } = useControls();
  const { config } = useAdParameters<any>();
  useHideCredits();
  const [assetRef, setAssetRef] = useState<HTMLImageElement | null>(null);
  useSetAdBackgroundColor('#fff');
  useSetAdIsDisplay(true);

  const { playState } = useVPAIDPlayState();
  useManualDuration(playState.isPlaying, 15, updateTime);

  const imgRatio = useImageRatio(assetRef);
  const assetRatio = useMemo(() => imgRatio && Math.max(0.8, imgRatio), [imgRatio]);
  useMaxElementHeight(assetRef, assetRatio);

  const mainImageUri = useOptimizedAsset(config.asset);

  useEffect(() => {
    if (!videoSlot) return;
    videoSlot.style.display = 'none';
  }, [videoSlot]);

  const onClick = useClickthru(config.destination_url);

  const adSize = useAdSize();
  const imgRealSize = useResizeObserver(assetRef);

  useBlackBarSizeDetector(
    imgRealSize.width,
    imgRealSize.height,
    assetRef?.naturalWidth ?? 0,
    assetRef?.naturalHeight ?? 0
  );

  const areBarsVisible = shouldBarsBeVisible(
    adSize.width,
    adSize.width / (imgRatio || 1),
    imgRealSize.width,
    imgRealSize.height
  );

  const adRef = useRef<HTMLElement>(null);
  useUpdateSlotSizeWithMainImage(adRef.current, assetRef, imgRealSize);

  return (
    <CreativeComponent onClick={onClick} isRTL={isRTL} adRef={adRef}>
      {header}
      <img
        alt=""
        ref={setAssetRef}
        src={mainImageUri}
        id={AssetComponentId}
        style={{
          flex: '1 1 auto',
          display: 'flex',
          overflow: 'hidden',
          justifyContent: 'space-between',
          margin: '-1px 0px',
          maxWidth: '100%',
          objectFit: 'contain',
          background: areBarsVisible ? '#000000' : 'transparent',
          minHeight: 0,
        }}
      />
      {footer}
    </CreativeComponent>
  );
}
